import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ArrowRight, CalendarDays } from "lucide-react";
import { FadeIn, SplitText, SpotlightCard, TiltCard, HexPattern, Eyebrow, FloatingHexes } from "../components/reactbits/Anim";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  } catch {
    return iso;
  }
};

const Blogs = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blogs`);
      setPosts(res.data);
    } catch (e) {
      console.error("Failed to load blogs", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const CardInner = ({ post }) => (
    <SpotlightCard className="group h-full overflow-hidden rounded-2xl border border-[#EDE5D3] bg-white shadow-[0_2px_16px_rgba(31,27,19,0.04)] transition-all duration-300 hover:border-[#F0A322]/50 hover:shadow-[0_8px_30px_rgba(232,147,12,0.12)]">
      {post.thumbnail && (
        <div className="overflow-hidden">
          <img src={post.thumbnail} alt={post.title} className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      )}
      <div className="p-7">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#8A8272]">
          <CalendarDays className="h-3.5 w-3.5 text-[#E8930C]" /> {fmtDate(post.created_at)}
        </p>
        <h3 className="mt-3 font-heading text-xl font-semibold leading-snug text-[#1F1B13] transition-colors duration-200 group-hover:text-[#B45309]">
          {post.title}
        </h3>
        {post.excerpt && <p className="mt-2.5 text-sm leading-relaxed text-[#6B6353] line-clamp-3">{post.excerpt}</p>}
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#B45309]">
          Read More
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </SpotlightCard>
  );

  return (
    <div className="bg-white text-[#1F1B13]" data-testid="blogs-page">
      <section className="relative overflow-hidden bg-[#FDFBF7] pt-40 pb-20">
        <HexPattern opacity={0.07} />
        <FloatingHexes />
        <div
          className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full"
          style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.13), transparent 60%)" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Eyebrow>insights</Eyebrow>
          <h1 className="mt-6 font-heading text-4xl font-bold uppercase leading-tight sm:text-6xl" data-testid="blogs-heading">
            <span className="text-[#E8930C]">
              <SplitText text="BLOGS" />
            </span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5D564A] sm:text-lg">
            Insights on AI, regulatory compliance, and the future of medical device R&D.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {loading ? (
            <p className="text-sm text-[#8A8272]" data-testid="blogs-loading">Loading posts…</p>
          ) : posts.length === 0 ? (
            <p className="text-sm text-[#8A8272]" data-testid="blogs-empty">No posts published yet — check back soon.</p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="blogs-grid">
              {posts.map((post, i) => (
                <FadeIn key={post.id} delay={(i % 3) * 0.08} className="h-full">
                  <TiltCard className="h-full">
                    {post.content ? (
                      <Link to={`/blogs/${post.slug}`} data-testid={`blog-post-${i}`} className="block h-full">
                        <CardInner post={post} />
                      </Link>
                    ) : post.external_url ? (
                      <a href={post.external_url} target="_blank" rel="noreferrer" data-testid={`blog-post-${i}`} className="block h-full">
                        <CardInner post={post} />
                      </a>
                    ) : (
                      <Link to={`/blogs/${post.slug}`} data-testid={`blog-post-${i}`} className="block h-full">
                        <CardInner post={post} />
                      </Link>
                    )}
                  </TiltCard>
                </FadeIn>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
